<script lang="ts">
  import type {
    Debtor,
    DebtorAccess,
    DebtorData,
  } from "$apis/parkito2/index.ts";
  import {
    Button,
    CardInline,
    Header,
    Hor,
    Label,
    ListNavigationItem,
    ShortDebtorSummary,
    Spacer,
    Toast,
    Ver,
  } from "$components";
  import { Form } from "$shared/lib/validation_next";
  import api, { fromApi } from "$shared/lib/api-parkito2.svelte.ts";
  import type { Writable } from "svelte/store";
  import DebtorForm, {
    type DebtorFormOutput,
  } from "$shared/components/parkito/debtors/DebtorForm.svelte";
  import { MyPagesStartModel } from "$models";

  type Props = {
    selectedDebtor: Writable<Debtor | undefined>;
    myPages: MyPagesStartModel;
    reloadDebtors: () => Promise<unknown>;
    editable?: boolean;
    onDoneEditing?: () => void;
  };
  const {
    onDoneEditing,
    reloadDebtors,
    selectedDebtor,
    myPages,
    editable = true,
  }: Props = $props();
  let editDebtor = $state(myPages.debtors.length === 0);
  $effect(() => {
    if (editDebtor === false) {
      document.getElementById("debtor-summary-line")?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  });
  export function isEditing() {
    return editDebtor;
  }
  const createDebtor = fromApi((debtor: DebtorData) =>
    api.debtorCreate({ body: debtor })
  );
  const updateDebtor = fromApi((debtor: DebtorData, debtorId: string) =>
    api.debtorPut({ body: debtor, path: { id: debtorId } })
  );
  $createDebtor.onOk((deb) => {
    selectedDebtor.set(deb);
    doneEditing();
  });
  $updateDebtor.onOk((deb) => {
    selectedDebtor.set(deb);
    doneEditing();
  });
  const debtorForm = Form.input<DebtorFormOutput>();
  export function isOk() {
    return $debtorForm.res.isOk();
  }

  export function isLoading() {
    return $createDebtor.isLoading() || $updateDebtor.isLoading();
  }

  debtorForm.subscribe((deb) => {
    if (deb.res.isOk() && deb.res.value.type === "existing") {
      selectedDebtor.set(
        myPages.findDebtorById?.(deb.res.value.debtorId)
          ?.debtor,
      );
    }
  });
  const handleConfirmDebtor = async () => {
    if ($debtorForm.res.isErr()) {
      Toast.error("Fel i formulär");
      return;
    }

    const debtorForm = $debtorForm.res.value;
    // Upsert debtor
    if (debtorForm.type === "existing") {
      if (debtorForm.editDebtor !== null) {
        $updateDebtor.load(debtorForm.editDebtor, debtorForm.debtorId);
      }
    } else if (debtorForm.type === "new") {
      await $createDebtor.load(debtorForm.debtor);
    }

    reloadDebtors();
  };

  function doneEditing() {
    editDebtor = false;
    if (onDoneEditing) {
      onDoneEditing();
    }
  }
</script>

<div class:hidden={!editDebtor}>
  <CardInline
    title="Ändra profil"
    buttonTitle={$selectedDebtor ? "Avbryt" : undefined}
    buttonClick={doneEditing}
  >
    <DebtorForm
      title={false}
      selectableDebtors={myPages.debtors as DebtorAccess[]}
      debtorOutput={debtorForm}
    />

    <div class:invisible={!$createDebtor.isErr()}>
      <Label
        type="error"
        text={"Kunde inte spara kundkort, prova igen."}
      />
    </div>

    <Hor>
      <Button
        type="ghost"
        disabled={!$selectedDebtor}
        onClick={doneEditing}
        text="Avbryt"
      />
      <Spacer />

      <Button
        disabled={$debtorForm.res.isErr()}
        onClick={handleConfirmDebtor}
        text="Spara"
      />
    </Hor>
  </CardInline>
</div>

<div class:hidden={editDebtor}>
  <Ver>
    {#if !!$selectedDebtor}
      <div id="debtor-summary-line"></div>
      <ShortDebtorSummary debtor={$selectedDebtor} />
      <div class:hidden={!$updateDebtor.isErr()}>
        <Label
          type="error"
          text={"Kunde inte spara kundkort, prova igen."}
        />
      </div>
      {#if editable}
        <Button
          text="Ändra uppgifter"
          onClick={() => editDebtor = true}
        ></Button>
      {/if}
    {:else}
      <ListNavigationItem
        isSingleItem
        title="Profil saknas"
        description="Tryck här för att lägga till din information"
        link={() => editDebtor = true}
      />
    {/if}
  </Ver>
</div>
